import type { Tour } from "@/types/tour";
import { getLowestPrice } from "@/lib/tourUtils";

const SITE_URL = "https://topcarib.com";

export interface BreadcrumbItem {
  name: string;
  url: string;
}

/**
 * BreadcrumbList JSON-LD generator.
 * Pass items in order from root → current page. `url` should be absolute or root-relative.
 */
export function breadcrumbJsonLd(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`,
    })),
  };
}

export interface QuickFact {
  label: string;
  value: string;
}

/**
 * Build "Quick Facts" rows from a tour record — short, AI-citation-friendly
 * key/value pairs synthesized from existing structured fields.
 */
export function quickFactsFromTour(tour: Tour): QuickFact[] {
  const facts: QuickFact[] = [];
  const price = getLowestPrice(tour);

  if (price !== null) facts.push({ label: "Lowest price", value: `From $${price} USD` });

  if (tour.duration_hours !== null) {
    facts.push({
      label: "Duration",
      value: Number(tour.duration_hours) < 0 ? "Variable" : `${tour.duration_hours} hours`,
    });
  }

  if (tour.boat_type) facts.push({ label: "Boat type", value: tour.boat_type });
  if (tour.categories?.length) facts.push({ label: "Category", value: tour.categories.join(", ") });
  if (tour.departure_point) facts.push({ label: "Departure", value: `${tour.departure_point}, Aruba` });
  if (tour.pick_up) facts.push({ label: "Hotel pick-up", value: tour.pick_up });
  if (tour.time_of_day?.length) facts.push({ label: "Time of day", value: tour.time_of_day.join(", ") });
  if (tour.best_for?.length) facts.push({ label: "Best for", value: tour.best_for.join(", ") });
  if (tour.available_to?.length) facts.push({ label: "Available to", value: tour.available_to.join(", ") });

  if (tour.open_bar) {
    facts.push({
      label: "Drinks",
      value: tour.open_bar === "No alcohol" ? "No alcohol served" : "Open bar included",
    });
  }
  if (tour.inclusions?.length) {
    facts.push({ label: "What's included", value: tour.inclusions.slice(0, 6).join(", ") });
  }

  const platformCount = tour.tour_platforms?.length ?? 0;
  if (platformCount > 1) facts.push({ label: "Booking platforms", value: `${platformCount} compared` });

  return facts;
}

/**
 * FAQPage JSON-LD auto-generated from tour data. Returns null if no
 * meaningful Q&A can be derived.
 */
export function tourFaqJsonLd(tour: Tour) {
  const price = getLowestPrice(tour);
  const qa: { q: string; a: string }[] = [];

  if (price !== null) {
    const platformCount = tour.tour_platforms?.length ?? 0;
    qa.push({
      q: `How much does ${tour.title} cost?`,
      a:
        platformCount > 1
          ? `The lowest current price is $${price} USD, compared across ${platformCount} booking platforms on TopCarib.`
          : `The current price is $${price} USD.`,
    });
  }

  if (tour.duration_hours !== null) {
    const dur =
      Number(tour.duration_hours) < 0
        ? "The duration is variable — see the booking page for the option you choose."
        : `The tour lasts approximately ${tour.duration_hours} hours.`;
    qa.push({ q: `How long is ${tour.title}?`, a: dur });
  }

  if (tour.departure_point) {
    qa.push({
      q: `Where does ${tour.title} depart from?`,
      a: `The tour departs from ${tour.departure_point}, Aruba.${tour.pick_up && tour.pick_up !== "Not available" ? ` Hotel pick-up is ${tour.pick_up.toLowerCase()}.` : ""}`,
    });
  }

  if (tour.inclusions?.length) {
    qa.push({
      q: "What's included in this tour?",
      a: `Included: ${tour.inclusions.join(", ")}.`,
    });
  }

  if (tour.open_bar) {
    qa.push({
      q: "Is alcohol included?",
      a:
        tour.open_bar === "No alcohol"
          ? "No alcohol is served on this tour."
          : "Yes, an open bar is included on board.",
    });
  }

  if (tour.best_for?.length) {
    qa.push({
      q: "Who is this tour best for?",
      a: `Best suited for: ${tour.best_for.join(", ")}.`,
    });
  }

  if (tour.available_to?.length) {
    qa.push({
      q: "Can cruise passengers or hotel guests book this tour?",
      a: `This tour is available to: ${tour.available_to.join(" and ")}.`,
    });
  }

  qa.push({
    q: "Can I cancel or get a refund?",
    a: "Cancellation and refund policies depend on the booking platform you choose. Check the platform's terms on the booking page — most major platforms (Viator, GetYourGuide, Expedia) offer free cancellation up to 24 hours before the tour.",
  });

  if (qa.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: qa.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

export function jsonLdScript(schema: unknown) {
  return {
    __html: JSON.stringify(schema),
  };
}
